import React from 'react'

import { Box, Stack } from '@mui/material'
import { Card, Skeleton } from '@repo/ui'

export interface PlanMatrixSkeletonProps {
  /** Number of plan columns to mirror. Defaults to 3 (the Starter / Individual
   *  / Business spread the signup catalog typically returns per interval). */
  columns?: number
}

/**
 * Loading placeholder for the signup plan step's `PlanComparisonMatrix`
 * (working-conventions §19 — a skeleton occupies the SAME layout the loaded
 * content will, never a spinner or a blank area).
 *
 * It mirrors the matrix's actual structure rather than being one grey block:
 * the same `minmax(220px, 1.6fr) repeat(n, 1fr)` grid, a header band holding a
 * title / price / description / CTA per column, then a run of data rows with a
 * labelled first cell. That is what keeps the page from shifting when the real
 * catalog arrives.
 *
 * It replaces `PlanColumnsSkeleton`, which mirrored the old row-of-cards layout
 * that no longer exists (VRG-383).
 */
export const PlanMatrixSkeleton = ({ columns = 3 }: PlanMatrixSkeletonProps) => {
  // Same template the matrix computes for itself.
  const gridTemplateColumns = `minmax(220px, 1.6fr) repeat(${columns}, 1fr)`

  return (
    <Card>
      {/* Header band — the matrix folds its plan cards into these cells, so the
          skeleton has to reserve that whole height or the table below it jumps
          up by ~200px when the plans land. */}
      <Box sx={{ display: 'grid', gridTemplateColumns, alignItems: 'stretch' }}>
        <Box sx={{ pt: 5, pb: 4, pr: 4 }}>
          <Stack spacing={3}>
            <Skeleton width="60%" height={20} />
            <Skeleton width="90%" height={32} />
          </Stack>
        </Box>
        {Array.from({ length: columns }).map((_, index) => (
          <Stack
            key={index}
            spacing={3}
            alignItems="center"
            sx={{ pt: 5, pb: 4, px: 4 }}
          >
            <Skeleton width="60%" height={20} />
            <Skeleton width="70%" height={36} />
            {/* The "Pays for itself" subline's reserved 60px + the
                description's fixed 42px, as one three-line block. */}
            <Skeleton width="100%" lines={3} />
            <Skeleton width="100%" height={40} />
          </Stack>
        ))}
      </Box>

      {/* A representative run of comparison rows. */}
      {Array.from({ length: 6 }).map((_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: 'grid',
            gridTemplateColumns,
            alignItems: 'center',
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            py: 3,
            pr: 3,
          }}
        >
          <Skeleton width="55%" height={16} />
          {Array.from({ length: columns }).map((_, columnIndex) => (
            <Box
              key={columnIndex}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Skeleton width={40} height={16} />
            </Box>
          ))}
        </Box>
      ))}
    </Card>
  )
}
